import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, RefreshCw } from 'lucide-react';
import { METRICS, type MetricKey } from '@raptor/shared';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/EmptyState';
import { LoadingState } from '@/components/LoadingState';
import { SectionLabel } from '@/components/SectionLabel';
import { useChartCards, useSaveChartCards, wellsReadingsQueryKey } from '../api';
import { ReadingsChartCard } from './ReadingsChartCard';
import type { ChartCardConfig } from '../types';

const SAVE_DEBOUNCE_MS = 800;
const FIRST_CHARTED_METRIC = (Object.keys(METRICS) as MetricKey[]).find((k) => METRICS[k].charted)!;

function newCardId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `card-${Math.round(performance.now())}-${Date.now()}`;
}

function makeCard(wellId: string): ChartCardConfig {
  return { id: newCardId(), wellId, metric: FIRST_CHARTED_METRIC, rangePreset: '7d' };
}

interface Props {
  wells: Array<{ id: string; name: string }>;
}

export function ChartsSection({ wells }: Props) {
  const layout = useChartCards();
  const save = useSaveChartCards();
  const qc = useQueryClient();

  const [cards, setCards] = useState<ChartCardConfig[]>([]);
  const initialized = useRef(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout>>();

  // Seed local state once the persisted layout loads. First visit (no saved
  // cards) gets a sensible starter — first well + temperature — so the feature
  // demonstrates itself rather than showing a blank canvas. The starter is not
  // auto-persisted; the first real edit saves it.
  useEffect(() => {
    if (initialized.current || !layout.data) return;
    const persisted = layout.data.cards;
    if (persisted.length > 0) {
      setCards(persisted);
    } else if (wells.length > 0) {
      setCards([makeCard(wells[0].id)]);
    }
    initialized.current = true;
  }, [layout.data, wells]);

  // Clear any pending debounced save on unmount.
  useEffect(() => () => clearTimeout(saveTimer.current), []);

  // Debounced persist — never PUT on every keystroke/drag frame.
  const scheduleSave = useCallback(
    (next: ChartCardConfig[]) => {
      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => save.mutate({ cards: next }), SAVE_DEBOUNCE_MS);
    },
    [save],
  );

  const commit = useCallback(
    (next: ChartCardConfig[]) => {
      setCards(next);
      scheduleSave(next);
    },
    [scheduleSave],
  );

  const addCard = () => wells.length > 0 && commit([...cards, makeCard(wells[0].id)]);
  const removeCard = (id: string) => commit(cards.filter((c) => c.id !== id));
  const updateCard = (id: string, patch: Partial<ChartCardConfig>) =>
    commit(cards.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  const moveCard = (id: string, dir: -1 | 1) => {
    const i = cards.findIndex((c) => c.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= cards.length) return;
    const next = [...cards];
    [next[i], next[j]] = [next[j], next[i]];
    commit(next);
  };

  const refresh = () => qc.invalidateQueries({ queryKey: wellsReadingsQueryKey });

  if (layout.isLoading) {
    return <LoadingState rows={3} />;
  }

  return (
    <div className="space-y-4" data-testid="charts-section">
      <div className="flex items-center justify-between">
        <SectionLabel>Charts</SectionLabel>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            data-testid="charts-refresh"
            onClick={() => void refresh()}
          >
            <RefreshCw className="mr-1 h-3.5 w-3.5" /> Refresh
          </Button>
          <Button size="sm" data-testid="charts-add" onClick={addCard}>
            <Plus className="mr-1 h-3.5 w-3.5" /> Add chart
          </Button>
        </div>
      </div>

      {cards.length === 0 ? (
        <EmptyState
          title="No charts yet"
          description="Add a chart to plot a metric over time."
          action={
            <Button size="sm" variant="outline" onClick={addCard}>
              Add chart
            </Button>
          }
        />
      ) : (
        <div className="grid gap-4 xl:grid-cols-2">
          {cards.map((card, i) => (
            <ReadingsChartCard
              key={card.id}
              card={card}
              wells={wells}
              onChange={(patch) => updateCard(card.id, patch)}
              onRemove={() => removeCard(card.id)}
              onMoveUp={() => moveCard(card.id, -1)}
              onMoveDown={() => moveCard(card.id, 1)}
              canMoveUp={i > 0}
              canMoveDown={i < cards.length - 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}
